import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  NotificationActionType,
  useAcknowledgeNotification,
  useArchiveNotification,
  UserNotificationWithDetails,
  useUserNotifications,
} from 'hooks/useUserNotifications';
import {
  NotificationWithMetadataDto,
  useArchiveNotificationForAll,
  useNotificationsByGroup,
  NotificationPriority,
} from 'hooks/useNotification';
import { Card as BootstrapCard } from 'react-bootstrap';
import { Button } from '@/components/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import NotificationsFilterBar from './NotificationsFilterBar';
import { NotificationDeliveryPanel } from './NotificationDeliveryPanel';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Map as MapIcon } from 'lucide-react';
import { UserNotificationArchiveAction } from './UserNotificationArchiveAction';
import NotificationAttachedFiles from './NotificationAttachedFiles';
import ReviewRequestedLink from './ReviewRequestedNavlink';
import { renderAppMessageWithTaskLinks } from './appMessageTaskLinks';
import { registerFocNotificationsTourApi } from '../../focNotificationsTour/focNotificationsTourBridge';
import { MemphisThreatRichDetails } from './MemphisThreatRichDetails';
import { UscgFocSoutheastThreatRichDetails } from './UscgFocSoutheastThreatRichDetails';
import { FemaThunderboltInviteActions } from './FemaThunderboltInviteActions';
import {
  FEMA_THUNDERBOLT_INVITE_NOTIFICATION_ID,
  isHardcodedFemaThunderboltInvite,
} from './femaThunderboltInvite';
import {
  isHardcodedMemphisThreatNotification,
  memphisCreateIncidentPrefill,
  MEMPHIS_THREAT_NOTIFICATION_ID,
} from './memphisThreatNotification';
import {
  EPA_GULF_THREAT_NOTIFICATION_ID,
  isHardcodedEpaGulfThreatNotification,
} from './epaGulfThreatNotification';
import {
  FACILITY_DEMO_PACKS,
  getFacilityDemoPack,
  isFacilityDemoNotificationId,
} from './facilityDemoPack';
import { isHardcodedHeleneConeNotification } from './heleneConeNotification';
import {
  isHardcodedUscgFocSeThreatNotification,
  USCG_FOC_SE_THREAT_NOTIFICATION_ID,
} from './uscgFocSoutheastThreatNotification';
import {
  formatNotificationTimestamp,
  isSitrepWorkflowNotification,
  NotificationSeverityBadge,
  ReviewerCommentsBlock,
  severityLabel,
} from './notificationSeverityUi';
import { useNavigate, useParams } from 'react-router';
import { useActivationActions } from 'hooks/incident/useActivationActions';
import {
  useHubChromeEnabled,
  useNotificationDashboardFlag,
  useTennesseeMemphisDemoEnabled,
  useTropicalDemoKind,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import {
  findFacilityDemoOrgEvent,
  findEpaGulfOrgEvent,
  findHeleneConeOrgEvent,
  findTnngMemphisOrgEvent,
  findUscgFocSeOrgEvent,
  useOrgEvents,
} from 'hooks/orgEvents/orgEventHooks';
import { isTropicalThreatNotification } from '../EventsTab/tropicalThresholdHelpers';
import { cn } from '@/lib/utils';

type NotificationsFilter = 'active' | 'historical' | 'sent';

type NotificationsListProps = {
  filter: NotificationsFilter;
  hideFilters?: boolean;
  unreadIds?: Set<string>;
};

const getSeverityBadgeClass = (priority: string | undefined): string => {
  if (!priority) return 'bg-secondary';

  switch (priority.toLowerCase()) {
    case NotificationPriority.STAND_DOWN_SAFETY:
    case NotificationPriority.CRITICAL:
      return 'bg-danger';
    case NotificationPriority.WARNING:
      return 'bg-warning text-dark';
    case NotificationPriority.WATCH:
      return 'bg-success';
    case NotificationPriority.ADVISORY:
      return 'bg-primary';
    case NotificationPriority.INFO:
      return 'bg-secondary';
    default:
      return 'bg-secondary';
  }
};

const NotificationsList: React.FC<NotificationsListProps> = ({
  filter,
  hideFilters = false,
  unreadIds,
}) => {
  const demoKind = useTropicalDemoKind();
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  const hubChrome = useHubChromeEnabled();
  const dashboardEnabled = useNotificationDashboardFlag();
  const userGuid = useAppSelector(selectUserGuid);
  const navigate = useNavigate();
  const { incidentId } = useParams<{ incidentId: string }>();
  const { data: orgEvents = [] } = useOrgEvents({
    enabled: demoKind != null || tennesseeMemphisDemo || uscgFocEnabled,
  });
  const facilityPack = getFacilityDemoPack(demoKind);
  const linkedEvent = uscgFocEnabled
    ? findUscgFocSeOrgEvent(orgEvents)
    : tennesseeMemphisDemo
      ? findTnngMemphisOrgEvent(orgEvents)
      : facilityPack
        ? findFacilityDemoOrgEvent(orgEvents, facilityPack)
        : demoKind === 'epa'
          ? findEpaGulfOrgEvent(orgEvents)
          : findHeleneConeOrgEvent(orgEvents);
  const openCreateIncident = () =>
    navigate('/create/incident', {
      state: memphisCreateIncidentPrefill(
        linkedEvent?.id,
        tennesseeMemphisDemo ? 'tnng' : 'cfa'
      ),
    });
  const {
    data: userNotifications = [],
    isLoading: isUserNotificationsLoading,
  } = useUserNotifications();
  const { data: notifications = [], isLoading: isNotificationsLoading } =
    useNotificationsByGroup();
  const acknowledgeNotificationMutation = useAcknowledgeNotification();
  const isLoading = isNotificationsLoading || isUserNotificationsLoading;
  const archiveNotificationForAllMutation = useArchiveNotificationForAll();
  const archiveUserNotification = useArchiveNotification();
  const { mutateAsync: handleActivationUpdate } = useActivationActions();

  const [searchText, setSearchText] = useState('');
  const [selectedAorId, setSelectedAorId] = useState<UUID | null>(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState<UUID | null>(
    incidentId ?? null
  );
  const effectiveIncidentId = incidentId ?? selectedIncidentId;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () =>
      new Set([
        MEMPHIS_THREAT_NOTIFICATION_ID,
        EPA_GULF_THREAT_NOTIFICATION_ID,
        FEMA_THUNDERBOLT_INVITE_NOTIFICATION_ID,
        USCG_FOC_SE_THREAT_NOTIFICATION_ID,
        ...Object.values(FACILITY_DEMO_PACKS).map(
          (pack) => pack.notificationId
        ),
      ])
  );
  const [deliveryReportFor, setDeliveryReportFor] =
    useState<NotificationWithMetadataDto | null>(null);
  // With the dashboard on, the report is a page that can be linked and shared;
  // without it, the modal remains the only way to see delivery.
  const openDeliveryReport = (item: NotificationWithMetadataDto) => {
    if (dashboardEnabled) {
      navigate(`/notifications/sends/${item.id}`);
      return;
    }
    setDeliveryReportFor(item);
  };
  const filteredUserNotifications = useMemo(() => {
    let result = userNotifications.filter((un) =>
      filter === 'historical' ? un.archived : !un.archived
    );
    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          (item.appMessage || '').toLowerCase().includes(query) ||
          (item.createdByName || '').toLowerCase().includes(query) ||
          (item.reviewerComments || []).some((entry) =>
            `${entry.comment} ${entry.reviewerName || ''} ${entry.seatName || ''}`
              .toLowerCase()
              .includes(query)
          )
      );
    }

    if (selectedAorId) {
      result = result.filter(
        (n) =>
          isHardcodedMemphisThreatNotification(n.notificationId) ||
          isHardcodedEpaGulfThreatNotification(n.notificationId) ||
          isFacilityDemoNotificationId(n.notificationId) ||
          isHardcodedHeleneConeNotification(n.notificationId) ||
          isHardcodedFemaThunderboltInvite(n.notificationId) ||
          isHardcodedUscgFocSeThreatNotification(n.notificationId) ||
          n.aors?.includes(selectedAorId)
      );
    }

    if (effectiveIncidentId) {
      result = result.filter(
        (n) =>
          isHardcodedMemphisThreatNotification(n.notificationId) ||
          isHardcodedEpaGulfThreatNotification(n.notificationId) ||
          isFacilityDemoNotificationId(n.notificationId) ||
          isHardcodedFemaThunderboltInvite(n.notificationId) ||
          isHardcodedUscgFocSeThreatNotification(n.notificationId) ||
          n.incidentId === effectiveIncidentId
      );
    }
    return result;
  }, [
    userNotifications,
    filter,
    searchText,
    selectedAorId,
    effectiveIncidentId,
  ]);

  const filteredSentNotifications = useMemo(() => {
    let result = notifications.filter((n) => n.createdBy === userGuid);
    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (item) =>
          (item.metadata?.emailSubject || '').toLowerCase().includes(query) ||
          (item.metadata?.emailMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.appMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.teamsMessage || '').toLowerCase().includes(query) ||
          (item.metadata?.smsMessage || '').toLowerCase().includes(query)
      );
    }

    if (selectedAorId) {
      result = result.filter((n) => n.aors?.includes(selectedAorId));
    }

    if (effectiveIncidentId) {
      result = result.filter((n) => n.incidentId === effectiveIncidentId);
    }

    result.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

    return result;
  }, [notifications, searchText, userGuid, selectedAorId, effectiveIncidentId]);

  const handleAcknowledgement = async (notificationId: string) => {
    if (
      isHardcodedMemphisThreatNotification(notificationId) ||
      isHardcodedEpaGulfThreatNotification(notificationId) ||
      isFacilityDemoNotificationId(notificationId) ||
      isHardcodedHeleneConeNotification(notificationId) ||
      isHardcodedUscgFocSeThreatNotification(notificationId)
    )
      return;
    await acknowledgeNotificationMutation.mutateAsync({ notificationId });
  };

  const handleArchiveForAll = async (notificationId: string) => {
    if (
      isHardcodedMemphisThreatNotification(notificationId) ||
      isHardcodedEpaGulfThreatNotification(notificationId) ||
      isFacilityDemoNotificationId(notificationId) ||
      isHardcodedHeleneConeNotification(notificationId) ||
      isHardcodedUscgFocSeThreatNotification(notificationId)
    )
      return;
    await archiveNotificationForAllMutation.mutateAsync({ notificationId });
  };

  const handleActivation = async (
    notification: UserNotificationWithDetails
  ) => {
    if (
      isHardcodedMemphisThreatNotification(notification.notificationId) ||
      isHardcodedEpaGulfThreatNotification(notification.notificationId) ||
      isFacilityDemoNotificationId(notification.notificationId) ||
      isHardcodedHeleneConeNotification(notification.notificationId) ||
      isHardcodedUscgFocSeThreatNotification(notification.notificationId)
    ) {
      return;
    }
    if (!notification.incidentId) return;

    await handleActivationUpdate({
      incidentId: notification.incidentId,
      memberUserGuid: userGuid,
      isActivated: true,
    });

    await archiveUserNotification.mutateAsync({
      notificationId: notification.notificationId,
    });
  };

  const formatTimestamp = formatNotificationTimestamp;

  const setExpanded = (id: string, open: boolean) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  useEffect(() => {
    return registerFocNotificationsTourApi({
      expandFirstNotification: () => {
        const first = filteredUserNotifications[0];
        if (first) setExpanded(first.notificationId, true);
      },
      openFirstDeliveryReport: () => {
        const first = filteredSentNotifications[0];
        if (first) openDeliveryReport(first);
      },
    });
  }, [filteredSentNotifications, filteredUserNotifications]);

  const renderUserNotificationShadcn = (item: UserNotificationWithDetails) => {
    const isTropical = isTropicalThreatNotification(item, orgEvents);
    const key = item.notificationId;
    const isOpen = expandedIds.has(key);

    return (
      <Item
        key={key}
        variant="outline"
        className="tw:flex-col tw:items-stretch tw:gap-0 tw:overflow-hidden tw:border-border tw:bg-card tw:p-0 tw:text-card-foreground tw:transition-colors tw:hover:bg-muted/40"
      >
        <Collapsible
          open={isOpen}
          onOpenChange={(open) => setExpanded(key, open)}
        >
          <div
            className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:px-3 tw:py-2.5"
            onClick={() => setExpanded(key, !isOpen)}
          >
            {unreadIds ? (
              unreadIds.has(item.notificationId) ? (
                <span
                  className="tw:size-2 tw:shrink-0 tw:rounded-full tw:bg-info"
                  aria-label="Unread"
                />
              ) : (
                <span className="tw:size-2 tw:shrink-0" aria-hidden />
              )
            ) : null}
            <ItemContent className="tw:min-w-0">
              <ItemTitle className="tw:line-clamp-3 tw:w-full tw:text-sm tw:font-medium tw:text-foreground">
                {renderAppMessageWithTaskLinks(item.appMessage)}
              </ItemTitle>
              <ItemDescription className="tw:text-xs">
                <span className="tw:font-medium">Source:</span>{' '}
                {item.createdByName || 'System'}
                <span className="tw:mx-1.5 tw:opacity-50">·</span>
                {formatTimestamp(item.sentAt)}
              </ItemDescription>
            </ItemContent>
            <ItemActions className="tw:shrink-0">
              <NotificationSeverityBadge
                priority={item.priority}
                label={
                  isSitrepWorkflowNotification(item) ? 'Report' : undefined
                }
              />
              <UserNotificationArchiveAction
                notificationId={item.notificationId}
                archived={item.archived}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Zoom map to notification"
                data-tour="notifications-zoom"
                onClick={(event) => event.stopPropagation()}
              >
                <MapIcon className="tw:h-4 tw:w-4" />
              </Button>
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Toggle notification details"
                    data-tour="notifications-expand"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ChevronDown
                      className={cn(
                        'tw:h-4 tw:w-4 tw:transition-transform',
                        isOpen && 'tw:rotate-180'
                      )}
                    />
                  </Button>
                }
              />
            </ItemActions>
          </div>
          <CollapsibleContent>
            <div className="tw:space-y-3 tw:border-t tw:border-border tw:px-3 tw:py-3">
              {isHardcodedUscgFocSeThreatNotification(item.notificationId) ? (
                <UscgFocSoutheastThreatRichDetails
                  linkedOrgEventId={item.sourceItemId}
                  onCreateIncident={openCreateIncident}
                />
              ) : isTropical ? (
                <MemphisThreatRichDetails
                  linkedOrgEventId={item.sourceItemId}
                  onCreateIncident={openCreateIncident}
                />
              ) : isSitrepWorkflowNotification(item) ? (
                <>
                  <ReviewerCommentsBlock comments={item.reviewerComments} />
                  {item.sourceItemId ? <ReviewRequestedLink {...item} /> : null}
                </>
              ) : (
                <>
                  <FemaThunderboltInviteActions
                    notificationId={item.notificationId}
                    archived={item.archived}
                  />
                  <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                    {item.acknowledgementRequired && !item.acknowledged ? (
                      <Button
                        type="button"
                        size="xs"
                        data-tour="notifications-acknowledge"
                        onClick={() =>
                          void handleAcknowledgement(item.notificationId)
                        }
                      >
                        Confirm Receipt
                      </Button>
                    ) : null}
                    {item.actionType ===
                      NotificationActionType.ACTIVATION_REQUEST &&
                    !item.archivedAt ? (
                      <Button
                        type="button"
                        size="xs"
                        onClick={() => void handleActivation(item)}
                      >
                        Activate
                      </Button>
                    ) : null}
                  </div>
                  {item.sourceItemId ? <ReviewRequestedLink {...item} /> : null}
                  {(item.acknowledged || item.archived) && (
                    <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
                      {item.acknowledged
                        ? `Confirmed ${formatTimestamp(item.acknowledgedAt)}`
                        : null}
                      {item.archived ? ' Archived' : null}
                    </p>
                  )}
                </>
              )}
              <NotificationAttachedFiles files={item.files} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Item>
    );
  };

  const renderSentNotificationShadcn = (item: NotificationWithMetadataDto) => {
    const key = item.id;
    const isOpen = expandedIds.has(key);
    const title =
      item.metadata?.appMessage ||
      `Notification sent ${formatTimestamp(item.createdAt)}`;

    return (
      <Item
        key={key}
        variant="outline"
        className="tw:flex-col tw:items-stretch tw:gap-0 tw:overflow-hidden tw:border-border tw:bg-card tw:p-0 tw:text-card-foreground tw:transition-colors tw:hover:bg-muted/40"
      >
        <Collapsible
          open={isOpen}
          onOpenChange={(open) => setExpanded(key, open)}
        >
          <div
            className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:px-3 tw:py-2.5"
            onClick={() => setExpanded(key, !isOpen)}
          >
            <ItemContent className="tw:min-w-0">
              <ItemTitle className="tw:line-clamp-3 tw:w-full tw:text-sm tw:font-medium tw:text-foreground">
                {title}
              </ItemTitle>
              <ItemDescription className="tw:text-xs">
                Sent · {formatTimestamp(item.createdAt)}
              </ItemDescription>
            </ItemContent>
            <ItemActions className="tw:shrink-0">
              <NotificationSeverityBadge priority={item.priority} />
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Toggle notification details"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ChevronDown
                      className={cn(
                        'tw:h-4 tw:w-4 tw:transition-transform',
                        isOpen && 'tw:rotate-180'
                      )}
                    />
                  </Button>
                }
              />
            </ItemActions>
          </div>
          <CollapsibleContent>
            <div className="tw:space-y-2 tw:border-t tw:border-border tw:px-3 tw:py-3 tw:text-xs tw:text-foreground">
              {item.metadata?.emailMessage ? (
                <div className="tw:space-y-0.5">
                  <p className="tw:m-0 tw:font-medium tw:text-muted-foreground">
                    Email
                  </p>
                  {item.metadata.emailSubject ? (
                    <p className="tw:m-0 tw:font-medium">
                      {item.metadata.emailSubject}
                    </p>
                  ) : null}
                  <p className="tw:m-0">{item.metadata.emailMessage}</p>
                </div>
              ) : null}
              {item.metadata?.smsMessage ? (
                <div className="tw:space-y-0.5">
                  <p className="tw:m-0 tw:font-medium tw:text-muted-foreground">
                    SMS
                  </p>
                  <p className="tw:m-0">{item.metadata.smsMessage}</p>
                </div>
              ) : null}
              {item.metadata?.teamsMessage ? (
                <div className="tw:space-y-0.5">
                  <p className="tw:m-0 tw:font-medium tw:text-muted-foreground">
                    Teams
                  </p>
                  <p className="tw:m-0">{item.metadata.teamsMessage}</p>
                </div>
              ) : null}
              <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  data-tour="notifications-delivery-report"
                  onClick={() => openDeliveryReport(item)}
                >
                  Delivery Report
                </Button>
                {item.hasUnarchivedRecipients ? (
                  <Button
                    type="button"
                    size="xs"
                    onClick={() => void handleArchiveForAll(item.id)}
                  >
                    Archive for All Recipients
                  </Button>
                ) : null}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Item>
    );
  };

  const renderUserNotificationLegacy = (item: UserNotificationWithDetails) => {
    const isTropical = isTropicalThreatNotification(item, orgEvents);

    return (
      <BootstrapCard key={item.notificationId} className="card-outlined mb-2">
        <BootstrapCard.Body>
          <div className="d-flex align-items-center mb-1 gap-2">
            {unreadIds?.has(item.notificationId) ? (
              <span
                className="tw:size-2 tw:shrink-0 tw:rounded-full tw:bg-info"
                aria-label="Unread"
              />
            ) : null}
            {item.priority && (
              <span
                className={`badge ${getSeverityBadgeClass(item.priority)} text-uppercase`}
                style={{ fontSize: '0.65rem' }}
              >
                {severityLabel(item.priority)}
              </span>
            )}
            <span>{renderAppMessageWithTaskLinks(item.appMessage)}</span>
            <div className="ms-auto">
              <UserNotificationArchiveAction
                notificationId={item.notificationId}
                archived={item.archived}
              />
            </div>
          </div>
          <div className="text-muted small">
            <span>{item.createdByName || 'System'}</span>
            <span className="mx-2">•</span>
            <span>{formatTimestamp(item.sentAt)}</span>
          </div>
          {item.reviewerComments && item.reviewerComments.length > 0 ? (
            <div className="mt-2">
              <ReviewerCommentsBlock comments={item.reviewerComments} />
            </div>
          ) : null}
          {isHardcodedUscgFocSeThreatNotification(item.notificationId) ? (
            <div className="mt-2">
              <UscgFocSoutheastThreatRichDetails
                linkedOrgEventId={item.sourceItemId}
                onCreateIncident={openCreateIncident}
              />
            </div>
          ) : isTropical ? (
            <div className="mt-2">
              <MemphisThreatRichDetails
                linkedOrgEventId={item.sourceItemId}
                onCreateIncident={openCreateIncident}
              />
            </div>
          ) : null}
          <FemaThunderboltInviteActions
            notificationId={item.notificationId}
            archived={item.archived}
          />
          {isSitrepWorkflowNotification(item) ? null : (
            <div className="d-flex mt-2 gap-2">
              {item.acknowledgementRequired && !item.acknowledged && (
                <StylishNewButton
                  className="button--primary button--sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcknowledgement(item.notificationId);
                  }}
                >
                  Confirm Receipt
                </StylishNewButton>
              )}
            </div>
          )}

          <div
            className="notification-content mt-2"
            style={{ marginBottom: '-0.5rem' }}
          >
            {item.sourceItemId && <ReviewRequestedLink {...item} />}

            {item.actionType === NotificationActionType.ACTIVATION_REQUEST &&
              !item.archivedAt && (
                <div className="d-flex mt-2 gap-2">
                  <StylishNewButton
                    className="button--primary button--sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivation(item);
                    }}
                  >
                    Activate
                  </StylishNewButton>
                </div>
              )}

            <div className="mt-2">
              <NotificationAttachedFiles files={item.files} />
            </div>
            <div className="small text-muted mt-2">
              {item.acknowledged && (
                <span className="me-3">
                  Confirmed {formatTimestamp(item.acknowledgedAt)}
                </span>
              )}
              {item.archived && <span>Archived</span>}
            </div>
          </div>
        </BootstrapCard.Body>
      </BootstrapCard>
    );
  };

  const renderSentNotificationLegacy = (item: NotificationWithMetadataDto) => {
    const notificationId = item.id;
    const createdAt = item.createdAt;
    const title = `Notification sent ${formatTimestamp(createdAt)}`;

    return (
      <BootstrapCard key={notificationId} className="card-outlined mb-2">
        <BootstrapCard.Body>
          <div className="d-flex align-items-center mb-1 gap-2">
            {item.priority && (
              <span
                className={`badge ${getSeverityBadgeClass(item.priority)} text-uppercase`}
                style={{ fontSize: '0.65rem' }}
              >
                {severityLabel(item.priority)}
              </span>
            )}
            <span>{title}</span>
          </div>

          <div
            className="notification-content mt-3"
            style={{ marginBottom: '-0.75rem' }}
          >
            {item.metadata?.appMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  In-App Message
                </div>
                <p className="mb-0">
                  {renderAppMessageWithTaskLinks(item.metadata.appMessage)}
                </p>
              </div>
            )}

            {item.metadata?.emailMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  Email Message
                </div>
                {item.metadata.emailSubject && (
                  <div className="fw-semibold mb-1">
                    {item.metadata.emailSubject}
                  </div>
                )}
                <p className="mb-0">{item.metadata.emailMessage}</p>
              </div>
            )}

            {item.metadata?.smsMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  SMS Message
                </div>
                <p className="mb-0">{item.metadata.smsMessage}</p>
              </div>
            )}

            {item.metadata?.teamsMessage && (
              <div className="mb-3">
                <div className="fw-semibold text-muted small mb-1">
                  Teams Message
                </div>
                <p className="mb-0">{item.metadata.teamsMessage}</p>
              </div>
            )}

            {!item.metadata?.appMessage &&
              !item.metadata?.emailMessage &&
              !item.metadata?.smsMessage &&
              !item.metadata?.teamsMessage && (
                <p className="text-muted">No message content available</p>
              )}
          </div>

          <div className="d-flex mt-2 flex-wrap gap-2">
            {/* The delivery report used to ride along with the CFA redesign.
                The dashboard flag is what gates it now, so orgs without CFA
                still get delivery visibility. */}
            {dashboardEnabled && (
              <StylishNewButton
                className="button--secondary button--sm"
                data-tour="notifications-delivery-report"
                onClick={(e) => {
                  e.stopPropagation();
                  openDeliveryReport(item);
                }}
              >
                Delivery Report
              </StylishNewButton>
            )}
            {item.hasUnarchivedRecipients && (
              <StylishNewButton
                className="button--primary button--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveForAll(item.id);
                }}
              >
                Archive for All Recipients
              </StylishNewButton>
            )}
          </div>
        </BootstrapCard.Body>
      </BootstrapCard>
    );
  };

  const emptyLabel = hubChrome ? (
    <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
      No notifications
    </p>
  ) : (
    <div>No notifications</div>
  );

  const loadingLabel = hubChrome ? (
    <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">Loading…</p>
  ) : (
    <div>Loading...</div>
  );

  return (
    <div>
      {hideFilters ? null : (
        <NotificationsFilterBar
          searchText={searchText}
          onSearchChange={setSearchText}
          selectedAorId={selectedAorId}
          onAorChange={setSelectedAorId}
          selectedIncidentId={effectiveIncidentId}
          onIncidentChange={(next) => {
            if (incidentId) return;
            setSelectedIncidentId(next);
          }}
          lockToIncidentId={incidentId}
        />
      )}

      <div className={hubChrome ? 'tw:space-y-2' : 'notifications-list'}>
        {isLoading
          ? loadingLabel
          : filter === 'sent'
            ? filteredSentNotifications.length
              ? filteredSentNotifications.map(
                  hubChrome
                    ? renderSentNotificationShadcn
                    : renderSentNotificationLegacy
                )
              : emptyLabel
            : filteredUserNotifications.length
              ? filteredUserNotifications.map(
                  hubChrome
                    ? renderUserNotificationShadcn
                    : renderUserNotificationLegacy
                )
              : emptyLabel}
      </div>

      {deliveryReportFor ? (
        <div data-tour="notifications-delivery-report">
          <NotificationDeliveryPanel
            notificationId={deliveryReportFor.id}
            title={
              deliveryReportFor.metadata?.emailSubject ||
              deliveryReportFor.metadata?.appMessage ||
              `Sent ${formatTimestamp(deliveryReportFor.createdAt)}`
            }
            onClose={() => setDeliveryReportFor(null)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default NotificationsList;
